import type { Device } from '@scio3/shared-types';

/**
 * Video Wall layout overwrite — Yes / No before an assignment replaces a screen's
 * `feature.videoWallLayout` (#2484). Pure helpers; the surfaces (Edit Screen, Edit Group,
 * Push to Screens) build a `VideoWallOverwriteInput`, call `askVideoWallOverwriteIfNeeded`,
 * and put the answer into the ONE assignment payload as `keepVideoWallLayout`
 * (`keepVideoWallLayoutInput`). The server honours it per device — tag / group / folder /
 * rule targets and scheduled pushes included — so every dialog offers Yes / No / close.
 * Spec: claude-dev/ted/2026-10-16-video-wall-issue-size/01-spec.md §1, §5, §11.
 */

export type VideoWallOverwriteMode = 'edit-screen' | 'edit-group' | 'push';

export type VideoWallOverwriteAnswer = 'replace' | 'keep' | 'cancel';

export type VideoWallKnownScreen = Pick<Device, '_id' | 'deviceName' | 'feature'>;

export interface VideoWallOverwriteInput {
  mode: VideoWallOverwriteMode;
  /** appType | commonType === COMMON_TYPE.VIDEO_WALL */
  isVideoWallAsset: boolean;
  /** edit-screen only: new currentAssetId !== device.currentAssetId */
  assetChanged: boolean;
  /** edit-screen only: the value about to be sent (D3) */
  currentLayout?: string | null;
  /** asset.meta.templateConfig when known (D4: best-effort) */
  incomingLayout?: string | null;
  /** [] when targets are tags / groups / rules / folder */
  knownScreens: VideoWallKnownScreen[];
}

export interface VideoWallOverwriteMessage {
  titleKey: string;
  bodyKey: string;
  params: Record<string, string>;
}

export const VIDEO_WALL_OVERWRITE_KEYS = {
  title: 'screens.videoWallOverwrite.title',
  bodyEditScreen: 'screens.videoWallOverwrite.bodyEditScreen',
  bodyBulk: 'screens.videoWallOverwrite.bodyBulk',
  yes: 'screens.videoWallOverwrite.yes',
  no: 'screens.videoWallOverwrite.no',
  incomingUnknown: 'screens.videoWallOverwrite.incomingUnknown',
} as const;

/** Exact trimmed compare (D10); empty / null / undefined all normalize to ''. */
function normalizeLayout(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function screenLayout(screen: VideoWallKnownScreen): string {
  return normalizeLayout(screen.feature?.videoWallLayout);
}

/**
 * Every known screen already carries exactly the incoming layout → the assignment would not
 * change anything, so no popup (D10). Unknown incoming (D4) or no known screens → false (ask).
 */
export function allKnownScreensAlreadyHave(input: VideoWallOverwriteInput): boolean {
  const incoming = normalizeLayout(input.incomingLayout);
  if (!incoming || input.knownScreens.length === 0) return false;
  return input.knownScreens.every((screen) => screenLayout(screen) === incoming);
}

export function shouldConfirmVideoWallOverwrite(input: VideoWallOverwriteInput): boolean {
  if (!input.isVideoWallAsset) return false;
  if (input.mode === 'edit-screen') {
    const current = normalizeLayout(input.currentLayout);
    if (!input.assetChanged || !current) return false;
    return current !== normalizeLayout(input.incomingLayout);
  }
  return !allKnownScreensAlreadyHave(input);
}

/**
 * Fills `incomingLayout` from `fetchIncoming` — but only when the gate would otherwise fire, so
 * a non-Video-Wall save never pays for the lookup. With the template known, the same-layout
 * short-circuit (D10) becomes reachable; `null` keeps the "unknown" wording (D4).
 */
export async function withIncomingLayout(
  input: VideoWallOverwriteInput,
  fetchIncoming: () => Promise<string | null>,
): Promise<VideoWallOverwriteInput> {
  if (!shouldConfirmVideoWallOverwrite(input)) return input;
  return { ...input, incomingLayout: await fetchIncoming() };
}

/**
 * Dialog copy for the surface. `params.incoming` is the raw layout string, or the
 * `incomingUnknown` i18n key when the asset's template is not known (D4) — the dialog
 * translates that one key, never arbitrary param values.
 */
export function videoWallOverwriteMessage(
  input: VideoWallOverwriteInput,
): VideoWallOverwriteMessage {
  const incoming =
    normalizeLayout(input.incomingLayout) || VIDEO_WALL_OVERWRITE_KEYS.incomingUnknown;
  if (input.mode === 'edit-screen') {
    return {
      titleKey: VIDEO_WALL_OVERWRITE_KEYS.title,
      bodyKey: VIDEO_WALL_OVERWRITE_KEYS.bodyEditScreen,
      params: { current: normalizeLayout(input.currentLayout), incoming },
    };
  }
  return {
    titleKey: VIDEO_WALL_OVERWRITE_KEYS.title,
    bodyKey: VIDEO_WALL_OVERWRITE_KEYS.bodyBulk,
    params: { incoming },
  };
}

/**
 * Shows the dialog only when the gate fires. `null` = no dialog was shown (non-Video-Wall
 * content, or D10 skipped it) — the assignment then goes out exactly as it did before #2484.
 */
export async function askVideoWallOverwriteIfNeeded(
  input: VideoWallOverwriteInput,
  ask: (message: VideoWallOverwriteMessage) => Promise<VideoWallOverwriteAnswer>,
): Promise<VideoWallOverwriteAnswer | null> {
  return shouldConfirmVideoWallOverwrite(input) ? ask(videoWallOverwriteMessage(input)) : null;
}

/**
 * The `keepVideoWallLayout` field for the ONE assignment payload: `true` on "No, keep current",
 * `false` on "Yes, replace", and ABSENT (not `undefined` — no key at all) when no dialog was
 * shown, so those payloads stay byte-identical to today's. 'cancel' never reaches a payload.
 */
export function keepVideoWallLayoutInput(answer: VideoWallOverwriteAnswer | null): {
  keepVideoWallLayout?: boolean;
} {
  if (answer === 'keep') return { keepVideoWallLayout: true };
  if (answer === 'replace') return { keepVideoWallLayout: false };
  return {};
}
